import { useRef, useEffect, useCallback } from 'react';

const WHEEL = 280;
const CX = WHEEL / 2;
const CY = WHEEL / 2;
const RINGS = [
  { innerR: 56,  outerR: 78,  s: 55, l: 26 },
  { innerR: 84,  outerR: 106, s: 65, l: 42 },
  { innerR: 112, outerR: 130, s: 60, l: 62 },
] as const;
const OUTER_BOUND = RINGS[2].outerR + 10;
const INNER_BOUND = RINGS[0].innerR - 10;
const CENTER_R = INNER_BOUND - 2;

function hueName(h: number): string {
  const names = ['Red','Orange','Yellow','Lime','Green','Mint','Cyan','Azure','Blue','Violet','Magenta','Rose'];
  return names[Math.round(h / 30) % 12];
}

export interface ColorEntry {
  key: string;
  label: string;
  hue: number;
  onChange: (hue: number) => void;
  sat: number;
  onSatChange: (sat: number) => void;
}

interface ColorPickerProps {
  cfg: ColorEntry[];
  activeKey: string;
  onSwitch: (key: string) => void;
  onClose: () => void;
}

export function ColorPicker({ cfg, activeKey, onSwitch, onClose }: ColorPickerProps) {
  const active = cfg.find(c => c.key === activeKey)!;
  const { hue, onChange, sat, onSatChange, label } = active;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDragging = useRef(false);
  const hueRef = useRef(hue);
  useEffect(() => {
    hueRef.current = hue;
  }, [hue]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, WHEEL, WHEEL);
    for (let deg = 0; deg < 360; deg++) {
      const a1 = (deg - 0.6 - 90) * (Math.PI / 180);
      const a2 = (deg + 0.6 - 90) * (Math.PI / 180);
      for (const ring of RINGS) {
        ctx.beginPath();
        ctx.arc(CX, CY, ring.outerR, a1, a2);
        ctx.arc(CX, CY, ring.innerR, a2, a1, true);
        ctx.closePath();
        ctx.fillStyle = `hsl(${deg},${ring.s * sat / 100}%,${ring.l}%)`;
        ctx.fill();
      }
    }

    const selRad = (hue - 90) * (Math.PI / 180);
    const notch = 4.5 * (Math.PI / 180);
    ctx.beginPath();
    ctx.arc(CX, CY, OUTER_BOUND, selRad - notch, selRad + notch);
    ctx.arc(CX, CY, INNER_BOUND, selRad + notch, selRad - notch, true);
    ctx.closePath();
    ctx.fillStyle = 'white';
    ctx.fill();

    ctx.beginPath();
    ctx.arc(CX, CY, CENTER_R, 0, Math.PI * 2);
    ctx.fillStyle = `hsl(${hue},${65 * sat / 100}%,42%)`;
    ctx.fill();

    ctx.font = `bold ${Math.round(CENTER_R * 0.8)}px system-ui`;
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('✓', CX, CY + 1);
  }, [hue, sat]);

  const pickFromClient = useCallback((clientX: number, clientY: number): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const rect = canvas.getBoundingClientRect();
    const scale = WHEEL / rect.width;
    const x = (clientX - rect.left) * scale - CX;
    const y = (clientY - rect.top) * scale - CY;
    const r = Math.sqrt(x * x + y * y);
    if (r <= CENTER_R) return true;
    if (r > OUTER_BOUND) return false;
    let h = Math.round(Math.atan2(y, x) * 180 / Math.PI + 90);
    if (h < 0) h += 360;
    if (h >= 360) h -= 360;
    onChange(h);
    return false;
  }, [onChange]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => { if (!isDragging.current) return; pickFromClient(e.clientX, e.clientY); };
    const onUp = () => { isDragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [pickFromClient]);

  useEffect(() => {
    const block = (e: WheelEvent) => e.preventDefault();
    document.addEventListener('wheel', block, { passive: false });
    return () => document.removeEventListener('wheel', block);
  }, []);

  const swatchBg = (key: string, h: number, s: number) =>
    key === 'bg'
      ? `hsl(${h}, ${35 * s / 100}%, 18%)`
      : `hsl(${h}, ${65 * s / 100}%, 42%)`;

  const statusText = sat === 0 ? `${label}: Grey` : `${label}: ${hueName(hue)} · ${hue}°`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end"
      style={{ background: 'transparent' }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-2xl p-4 pb-8 flex flex-col items-center gap-3 mx-auto"
        style={{ background: '#1e293b', maxWidth: 560 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Swatch switcher row */}
        <div className="flex gap-2 w-full">
          {cfg.map(({ key, label: lbl, hue: h, sat: s }) => (
            <div key={key} className="flex-1 flex flex-col items-center gap-0.5">
              <button
                onClick={() => onSwitch(key)}
                style={{
                  width: '100%', height: 26, borderRadius: 4,
                  background: swatchBg(key, h, s),
                  border: activeKey === key ? '2px solid #f59e0b' : '1px solid rgba(255,255,255,0.1)',
                }}
              />
              <span style={{ fontSize: 10, color: activeKey === key ? '#fcd34d' : '#94a3b8', lineHeight: 1 }}>{lbl}</span>
            </div>
          ))}
        </div>

        {/* Saturation slider */}
        <div className="flex items-center gap-2 w-full px-1">
          <span className="text-xs text-slate-500 shrink-0 w-8 text-right">Grey</span>
          <input
            type="range" min={0} max={100} value={sat}
            onChange={e => onSatChange(parseInt(e.target.value))}
            className="flex-1"
            style={{ '--track-color': sat === 0 ? '#64748b' : `hsl(${hue}, ${65 * sat / 100}%, 50%)` } as React.CSSProperties}
          />
          <span className="text-xs text-slate-500 shrink-0 w-8">Full</span>
        </div>

        <canvas
          ref={canvasRef}
          width={WHEEL}
          height={WHEEL}
          style={{ touchAction: 'none', maxWidth: '100%', cursor: 'crosshair' }}
          onMouseDown={e => {
            isDragging.current = true;
            if (pickFromClient(e.clientX, e.clientY)) {
              e.preventDefault();
              onClose();
            }
          }}
          onPointerDown={e => {
            if (e.pointerType === 'touch') {
              e.currentTarget.setPointerCapture(e.pointerId);
              if (pickFromClient(e.clientX, e.clientY)) {
                // Block the synthesized mouse/click that iOS Safari fires after
                // touchend — otherwise it lands on the swatch row behind the
                // (now unmounted) picker and immediately reopens it.
                e.preventDefault();
                onClose();
              }
            }
          }}
          onPointerMove={e => {
            if (e.pointerType === 'touch' && e.buttons !== 0) {
              pickFromClient(e.clientX, e.clientY);
            }
          }}
          onWheel={e => {
            let h = hueRef.current + (e.deltaY > 0 ? 3 : -3);
            if (h < 0) h += 360;
            if (h >= 360) h -= 360;
            onChange(h);
          }}
        />
        <div className="text-xs text-slate-400">{statusText}</div>
      </div>
    </div>
  );
}
